import { Plane, Raycaster, Vector3 } from "three";
import {
  IInputEventHandler,
  InputEventType,
  InputMultiPointerEvent,
  InputPointerType,
  IPinchArgs,
  ISwipeArgs,
} from "../utils/input-handler/interface";
import * as Utils from "../utils/utils";
import { Viewer } from "../viewer";
import { MeasurementManager } from "./MeasurementManager";
import { IPanelDraggable } from "./types";

/**
 * Handler that moves the measurement result panel (text sprite) by dragging.
 * Registered to InputHandler under "drag-panel" only while "move panel" mode is
 * active (viewer.startPanelDrag / finishPanelDrag) — panels are NOT draggable
 * by default. Supports every measurement implementing IPanelDraggable
 * (detected via hitPanel). (SC_QUICK-2187)
 */
export class PanelDragObservable implements IInputEventHandler {
  private _viewer: Viewer;
  private _measurementManager: MeasurementManager;

  private _dragging: IPanelDraggable;
  private _dragPlane: Plane;
  private _dragStartPoint: Vector3;
  private _startOffset: Vector3;
  private _suppressClick: boolean = false;

  /** When set, only the panel of this measurement id can be dragged. */
  public targetId?: number;

  constructor(viewer: Viewer, measurementManager: MeasurementManager) {
    this._viewer = viewer;
    this._measurementManager = measurementManager;
  }

  /** Abort an in-progress drag (e.g. when leaving move-panel mode mid-drag). */
  public cancelDrag(): void {
    if (!this._dragging) return;
    this._viewer.enableControl({ pan: true, rotate: true });
    this._dragging = undefined;
    this._dragPlane = undefined;
    this._dragStartPoint = undefined;
    this._startOffset = undefined;
  }

  /**
   * Flag for MeasurementSelector to ignore the browser click fired right after a drag.
   * Only true within the same event burst after END_DRAG.
   */
  public get suppressClick(): boolean {
    return this._suppressClick;
  }

  public get isDragging(): boolean {
    return !!this._dragging;
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    switch (pointer.eventType) {
      case InputEventType.START_DRAG: {
        if (pointer.pointerType !== InputPointerType.PRIMARY) return;
        if (this._measurementManager.isMeasuring) return;

        const raycaster = this.screenToRaycaster(event);
        const measurement = this.hitPanel(raycaster);
        if (!measurement) return;

        // Drag on a plane facing the camera (same as MeasurementEditObservable direction:"z")
        const plane = new Plane().setFromNormalAndCoplanarPoint(
          this._viewer.getCamera().getWorldDirection(new Vector3()),
          measurement.panelPosition
        );
        const startPoint = raycaster.ray.intersectPlane(plane, new Vector3());
        if (!startPoint) return;

        this._viewer.enableControl({ pan: false, rotate: false });
        this._dragging = measurement;
        this._dragPlane = plane;
        this._dragStartPoint = startPoint;
        this._startOffset = measurement.textOffset;
        break;
      }
      case InputEventType.DRAGGED: {
        if (!this._dragging) return;

        const raycaster = this.screenToRaycaster(event);
        const point = raycaster.ray.intersectPlane(
          this._dragPlane,
          new Vector3()
        );
        if (!point) return;

        const offset = point
          .sub(this._dragStartPoint)
          .add(this._startOffset);
        this._dragging.textOffset = offset;
        this._dragging.updateTextPosition();
        break;
      }
      case InputEventType.END_DRAG: {
        if (!this._dragging) return;

        this._viewer.enableControl({ pan: true, rotate: true });
        this._dragging = undefined;
        this._dragPlane = undefined;
        this._dragStartPoint = undefined;
        this._startOffset = undefined;

        // The browser fires click synchronously right after pointerup (before any timer),
        // so setTimeout(0) reliably resets the flag afterwards
        this._suppressClick = true;
        setTimeout(() => {
          this._suppressClick = false;
        }, 0);
        break;
      }
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    // Panel drag does not support multi-touch
  }

  private hitPanel(raycaster: Raycaster): IPanelDraggable | undefined {
    const hits: { measurement: IPanelDraggable; distance: number }[] = [];
    for (const measurement of this._measurementManager.measurementMaps.values()) {
      if (this.targetId !== undefined && measurement.id !== this.targetId) continue;
      // Duck-typing: only measurements implementing IPanelDraggable expose hitPanel
      const draggable = measurement as unknown as IPanelDraggable;
      if (typeof draggable.hitPanel !== "function") continue;
      if (measurement.getVisibility() === false) continue;

      const intersects = draggable.hitPanel(raycaster);
      if (intersects.length > 0) {
        hits.push({ measurement: draggable, distance: intersects[0].distance });
      }
    }
    return hits.sort((a, b) => a.distance - b.distance)[0]?.measurement;
  }

  private screenToRaycaster(event: PointerEvent): Raycaster {
    const coords = Utils.CalculateMousePosition(
      { clientX: event.clientX, clientY: event.clientY },
      this._viewer.getRenderer().domElement
    );
    const raycaster = new Raycaster();
    raycaster.setFromCamera(coords, this._viewer.getCamera());
    return raycaster;
  }
}
